import { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
} from '@ui-framework/index';
import CustomTabPanel from '@product-framework/components/CustomTabPanel';
import { getProviderName } from '../utils';
import {
  createdBy,
  createdDateTime,
  issueActivityData,
  newIssueStatus,
  oldIssueStatus,
} from './accessorFunctions';
import tableSchema from './schema/list.json';
import statusTableSchema from './schema/statusList.json';
import { ACTIVITY_TAB_FILTERS, buildActivityListApiUrl } from './utils';

const STATUS_AUDIT_TAB = 0;
const DATA_AUDIT_TAB = 1;
const EVENT_FEED_TAB = 2;

function a11yProps(index) {
  return {
    id: `issue-additional-info-tab-${index}`,
  };
}

function IssueActivityTable({
  includeActivityTypes,
  excludeActivityTypes,
  issueId,
  schema,
  storeName,
}) {
  const dataProvider = useDataProvider(getProviderName());
  const { formatMessage } = useLocalization();

  if (!issueId) {
    return null;
  }
  const activityDataProvider = {
    ...dataProvider,
    get: requestUrl => {
      const apiUrl = new URL(requestUrl);
      apiUrl.searchParams.delete('sortBy');
      return dataProvider.get(apiUrl.href);
    },
  };
  const listApiUrl = buildActivityListApiUrl({
    baseApiUrl: dataProvider.getApiUrl(),
    issueId,
    includeActivityTypes,
    excludeActivityTypes,
  });

  const listProps = {
    columnPrimaryId: 'id',
    storeName,
    dataProvider: activityDataProvider,
    schema,
    initialState: {
      showColumnFilters: false,
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    downloadApiUrl: null,
    listApiUrl,
    refreshKey: listApiUrl,
    accessorFunctions: {
      createdDateTime,
      createdBy: original => createdBy(original, formatMessage),
      issueActivityData: original => issueActivityData(original, formatMessage),
      newIssueStatus: original => newIssueStatus(original, formatMessage),
      oldIssueStatus: original => oldIssueStatus(original, formatMessage),
    },
  };

  return (
    <Box sx={{ p: 1 }}>
      <Grid2 sx={{ p: 1 }}>
        <CRUDComponent
          type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
          listProps={listProps}
        />
      </Grid2>
    </Box>
  );
}

function IssueAdditionalInfo({ issueId }) {
  const { formatMessage } = useLocalization();
  const [value, setValue] = useState(STATUS_AUDIT_TAB);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Grid2 xs={12}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange}>
            <Tab
              label={formatMessage({
                id: 'STATUS_AUDIT',
                defaultMessage: 'Status Audit',
              })}
              {...a11yProps(STATUS_AUDIT_TAB)}
              value={STATUS_AUDIT_TAB}
            />
            <Tab
              label={formatMessage({
                id: 'AUDIT_DATA',
                defaultMessage: 'Audit Data',
              })}
              {...a11yProps(DATA_AUDIT_TAB)}
              value={DATA_AUDIT_TAB}
            />
            <Tab
              label={formatMessage({
                id: 'EVENT_FEED',
                defaultMessage: 'Event Feed',
              })}
              {...a11yProps(EVENT_FEED_TAB)}
              value={EVENT_FEED_TAB}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={STATUS_AUDIT_TAB}>
          <IssueActivityTable
            issueId={issueId}
            {...ACTIVITY_TAB_FILTERS.STATUS_AUDIT}
            schema={statusTableSchema}
            storeName="IssueStatusAuditTable"
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={DATA_AUDIT_TAB}>
          <IssueActivityTable
            issueId={issueId}
            {...ACTIVITY_TAB_FILTERS.AUDIT_DATA}
            schema={tableSchema}
            storeName="IssueAuditDataTable"
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={EVENT_FEED_TAB}>
          <IssueActivityTable
            issueId={issueId}
            {...ACTIVITY_TAB_FILTERS.EVENT_FEED}
            schema={tableSchema}
            storeName="IssueEventFeedTable"
          />
        </CustomTabPanel>
      </Box>
    </Grid2>
  );
}

export default IssueAdditionalInfo;
